import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  SelectChangeEvent,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import MonitoringNoContent from '../../../../assets/images/monitoring_no_content.png';
import PlatformSelect from '../../../../components/PlatformSelect/PlatformSelect';
import React, { ChangeEvent, KeyboardEvent, useContext, useEffect, useState } from 'react';
import UrlValidationService from '../../../../services/UrlValidationService';
import { Link, useNavigate } from 'react-router-dom';
import { Check, InfoOutlined, Repeat } from '@mui/icons-material';
import Guide from '../../../../components/Guide/Guide';
import { IPlatform } from '../../../../shared/interfaces/IPlatform';
import ScrapeService from '../../../../services/ScrapeService';
import CompanyNameService from '../../../../services/CompanyNameService';
import PlatformService from '../../../../services/PlatformService';
import Section from '../../../../components/Main/Section/Section';
import ReviewMonitoringService from '../../../../services/ReviewMonitoringService';
import { IMonitoring } from '../../../../shared/interfaces/IMonitoring';
import { useTranslation } from 'react-i18next';
import BenchmarkingQuickSetup from '../../../../components/ReviewMonitoringGuide/BenchmarkingQuickSetup';
import CommunityManagementConfigurator from '../../../../components/ReviewMonitoringGuide/CommunityManagementConfigurator';
import BenchmarkingService from '../../../../services/BenchmarkingService';
import { ICompetitor } from '../../../../shared/interfaces/ICompetitor';
import { CommunityManagement } from '../../../../shared/interfaces/ICommunityManagement';
import UserService from '../../../../services/UserService';
import { CurrentViewContext } from '../../../../providers/CurrentViewProvider';
import { AuthContext } from '../../../../providers/AuthProvider';
import PlaceIDFinder from '../../../../components/PlaceIDFinder/PlaceIDFinder';

const ReviewMonitoringGuide = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasRole } = useContext(AuthContext);

  const [platform, setPlatform] = useState('');
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentMonitoringGuideStep, setCurrentMonitoringGuideStep] = useState(0);
  const [newMonitoringLabel, setNewMonitoringLabel] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [isCompanyUrlSubmitBtnDisabled, setIsCompanyUrlSubmitBtnDisabled] = useState(false);
  const [isValidationInProgress, setIsValidationInProgress] = useState(false);
  const [isInvalidCompanyUrl, setIsInvalidCompanyUrl] = useState(false);
  const [hasMonitoringSetUp, setHasMonitoringSetUp] = useState(false);
  const [configureBenchmarking, setConfigureBenchmarking] = useState(false);
  const [configureCommunityManagement, setConfigureCommunityManagement] = useState(false);
  const [competitors, setCompetitors] = useState<ICompetitor[]>([]);
  const [canConfigureCommunityManagement, setCanConfigureCommunityManagement] = useState(false);
  const [canConfigureBenchmarking, setCanConfigureBenchmarking] = useState(false);
  const [communityManagementSettings, setCommunityManagementSettings] =
    useState<CommunityManagement>({
      scoreThreshold: 1,
      languagePreference: 'de',
      salutationPrefix: '',
      useUsername: true,
      greeting: '',
      tonality: {
        preference: 0,
      },
      pronoun: {
        preference: 0,
      },
    });
  const availablePlatforms = ['googleplace', 'indeed', 'aerzte'];

  const getCompanyUrlLabel = (company: string) => {
    if (company === 'googleplace') return 'Ihre Google Place ID:';

    return t('company_url_label') + ' ' + platforms.find((v) => v.label === company)?.url;
  };

  const validateCompanyUrl = async () => {
    const isUrlValid = await UrlValidationService.validateUrl(
      platform === 'googleplace' ? `https://google.com/${companyUrl}` : companyUrl
    );
    const isPlatformAllowed = availablePlatforms.includes(platform);

    if (isUrlValid || isPlatformAllowed) {
      handleNextMonitoringGuideStep();
      setIsInvalidCompanyUrl(false);
      setIsCompanyUrlSubmitBtnDisabled(false);
      setCanConfigureBenchmarking(true);
    } else {
      setIsCompanyUrlSubmitBtnDisabled(true);
      setIsInvalidCompanyUrl(true);
    }
  };

  const handleMonitoringLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewMonitoringLabel(event.target.value);
  };

  const handleNextMonitoringGuideStep = () => {
    setCurrentMonitoringGuideStep((prevMonitoringGuideStep) => prevMonitoringGuideStep + 1);
  };

  const handlePreviousMonitoringGuideStep = () => {
    setCurrentMonitoringGuideStep((prevMonitoringGuideStep) => prevMonitoringGuideStep - 1);
  };

  const prefillMonitoringLabel = () => {
    if (newMonitoringLabel === '') setNewMonitoringLabel(platform);
  };

  function getFaqLink() {
    return process.env.REACT_APP_FAQ_LINK ?? '';
  }

  const alert = (
    <Alert
      severity="warning"
      sx={{
        maxWidth: '650px',
        my: theme.spacing(6),
      }}
    >
      <AlertTitle sx={{ fontWeight: '700' }}>{t('monitoring_alert_title')}</AlertTitle>
      {t('monitoring_alert_text')} <Link to={getFaqLink()}>FAQ´s.</Link>
    </Alert>
  );

  const startScraping = async () => {
    const platformId = PlatformService.getPlatformId(platform, platforms);
    const companyName = await CompanyNameService.getCompanyName(platformId, companyUrl);

    if (companyName === undefined) {
      handlePreviousMonitoringGuideStep();
      setIsCompanyUrlSubmitBtnDisabled(true);
      setIsInvalidCompanyUrl(true);
      return false;
    }

    await ScrapeService.startScraping(
      platform,
      companyName,
      newMonitoringLabel,
      companyUrl,
      false,
      communityManagementSettings
    );

    if (competitors.length > 0) {
      for (const competitor of competitors) {
        const companyName = await CompanyNameService.getCompanyName(platformId, competitor.url);
        if (companyName === undefined) {
          console.error('Could not retreive competitor company name!');
          return false;
        }
        await ScrapeService.startScraping(
          platform,
          companyName,
          competitor.label,
          competitor.url,
          true,
          null
        );
      }
      setConfigureBenchmarking((prevState) => {
        if (prevState) setCompetitors([]);
        return !prevState;
      });
      await BenchmarkingService.addBenchmark(
        platform,
        newMonitoringLabel,
        {
          label: newMonitoringLabel,
          company: companyName,
          url: companyUrl,
        },
        competitors
      );
    }

    setCurrentMonitoringGuideStep(0);
    setPlatform('');
    setNewMonitoringLabel('');
    setCompanyUrl('');
    setHasMonitoringSetUp(true);
    setCanConfigureBenchmarking(false);

    return true;
  };

  const getPlatforms = async (signal: AbortSignal) => {
    const platforms = await PlatformService.getPlatforms(setLoading, signal);
    if (!platforms) return;
    setPlatforms(platforms);
  };

  const checkHasMonitoringSetUp = async (signal: AbortSignal) => {
    const monitoring: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
      true,
      'both',
      setLoading,
      signal
    );
    if (!monitoring) return;
    setHasMonitoringSetUp(monitoring.length > 0);
  };

  useEffect(() => {
    setCurrentView(`${t('nav_reviews')} Guide`);
    document.title = `Reputativ ${t('nav_reviews')} Guide`;
  }, [setCurrentView, t]);

  useEffect(() => {
    const controller = new AbortController();

    const getCommunityManagementSettings = async () => {
      const response = await UserService.getCommunityManagementSettings(controller.signal);

      if (!response) return;
      if (!response.communityManagement) return;

      setCommunityManagementSettings(response.communityManagement);
    };

    (async () => await getPlatforms(controller.signal))();
    (async () => await checkHasMonitoringSetUp(controller.signal))();
    (async () => await getCommunityManagementSettings())();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Section>
      {loading && (
        <Box>
          <LinearProgress sx={{ my: theme.spacing(1) }} />
        </Box>
      )}
      <Guide
        image={
          <img
            src={MonitoringNoContent}
            width={300}
            style={{ marginBottom: !hasMonitoringSetUp ? 0 : `${theme.spacing(6)}` }}
            alt="Monitoring Guide Title"
          />
        }
        alert={!hasMonitoringSetUp && alert}
      >
        <Box sx={{ maxWidth: 600 }}>
          <Stepper
            activeStep={currentMonitoringGuideStep}
            orientation="vertical"
          >
            <Step>
              <StepLabel>{t('select_platform')}</StepLabel>
              <StepContent>
                <PlatformSelect
                  value={platform || ''}
                  onChange={(event: SelectChangeEvent) => {
                    setPlatform(event.target.value || '');
                  }}
                  platforms={platforms}
                />
                <Box sx={{ mb: 2 }}>
                  <Button
                    disabled={platform === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextMonitoringGuideStep();
                      setCanConfigureCommunityManagement(true);
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      navigate(-1);
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('cancel')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t('monitor_guide_label')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('optional_label')}
                  placeholder={`${t('example_company_placeholder')} (${platform})`}
                  variant="standard"
                  value={newMonitoringLabel}
                  onChange={handleMonitoringLabelChange}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter') {
                      prefillMonitoringLabel();
                      handleNextMonitoringGuideStep();
                    }
                  }}
                  sx={{ minWidth: '400px' }}
                  autoFocus
                />
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      prefillMonitoringLabel();
                      handleNextMonitoringGuideStep();
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    onClick={() => {
                      handlePreviousMonitoringGuideStep();
                      setCanConfigureCommunityManagement(false);
                      setConfigureCommunityManagement(false);
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel
                error={isInvalidCompanyUrl}
                optional={
                  isInvalidCompanyUrl && (
                    <Typography
                      variant="caption"
                      color="error"
                    >
                      {t('url_invalid')}
                    </Typography>
                  )
                }
                sx={{
                  '.MuiStepLabel-label': {
                    display: 'flex',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  component="span"
                >
                  {t('enter_company_url')}
                </Typography>
                <Tooltip
                  title={
                    platform === 'googleplace'
                      ? 'Geben Sie Ihre Google Place ID an, diese kann z. B. so aussehen: ChIJbUXH8GL2r0cRSruZ3Ui9YhQ.'
                      : t('company_url_tooltip')
                  }
                  placement="top"
                  arrow
                >
                  <InfoOutlined
                    fontSize="small"
                    sx={{
                      color: theme.palette.primary.main,
                      ml: theme.spacing(2),
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              </StepLabel>
              <StepContent>
                {platform === 'googleplace' ? (
                  <PlaceIDFinder onChange={setCompanyUrl} />
                ) : (
                  <TextField
                    label={getCompanyUrlLabel(platform)}
                    variant="standard"
                    value={companyUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setIsCompanyUrlSubmitBtnDisabled(false);
                      setIsInvalidCompanyUrl(false);
                      setCompanyUrl(event.target.value);
                    }}
                    onKeyDown={async (event: KeyboardEvent<HTMLDivElement>) => {
                      if (
                        event.key === 'Enter' &&
                        companyUrl !== '' &&
                        !isCompanyUrlSubmitBtnDisabled
                      ) {
                        setIsValidationInProgress(true);
                        await validateCompanyUrl();
                        setIsValidationInProgress(false);
                      }
                    }}
                    sx={{ minWidth: '400px' }}
                    autoFocus
                  />
                )}
                {isValidationInProgress && (
                  <Box>
                    <LinearProgress sx={{ my: theme.spacing(1) }} />
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Button
                    disabled={
                      companyUrl === '' ||
                      isInvalidCompanyUrl ||
                      isValidationInProgress ||
                      isCompanyUrlSubmitBtnDisabled
                    }
                    variant="contained"
                    onClick={async () => {
                      setIsValidationInProgress(true);
                      await validateCompanyUrl();
                      setIsValidationInProgress(false);
                    }}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    onClick={handlePreviousMonitoringGuideStep}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t('finish_configuration')}</StepLabel>
              <StepContent>
                {t('guide_info_text')}
                <Box sx={{ mb: 2 }}>
                  <div>
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<Repeat />}
                      onClick={async () => {
                        const success = await startScraping();
                        if (success) setHasMonitoringSetUp(true);
                      }}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      {t('another_entry')}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Check />}
                      onClick={async () => {
                        const success = await startScraping();
                        if (success) navigate(-1);
                      }}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      {t('finish')}
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
        <Box sx={{ display: 'flex', minWidth: '1px', minHeight: '300px', mx: theme.spacing(6) }}>
          <Divider
            orientation="vertical"
            flexItem
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'flex-start',
            minWidth: '400px',
          }}
        >
          <Typography
            variant="body2"
            sx={{ mb: theme.spacing(2) }}
          >
            {t('optional_configuration')}
          </Typography>
          {hasRole('can-use-ai') && (
            <>
              <FormGroup>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={!canConfigureCommunityManagement}
                        checked={configureCommunityManagement}
                        onChange={() => {
                          setConfigureCommunityManagement((prevState) => !prevState);
                        }}
                      />
                    }
                    label={<Typography variant="body2">{t('community_management')}</Typography>}
                  />
                  <Tooltip
                    title={t('configure_community_management')}
                    placement="right-end"
                    arrow
                  >
                    <InfoOutlined
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                    />
                  </Tooltip>
                </Box>
                {configureCommunityManagement && platform && (
                  <CommunityManagementConfigurator
                    settings={communityManagementSettings}
                    onChange={setCommunityManagementSettings}
                  />
                )}
              </FormGroup>
            </>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  disabled={!canConfigureBenchmarking}
                  checked={configureBenchmarking}
                  onChange={() => {
                    setConfigureBenchmarking((prevState) => {
                      if (prevState) setCompetitors([]);
                      return !prevState;
                    });
                  }}
                />
              }
              label={<Typography variant="body2">{t('benchmarking')}</Typography>}
            />
            <Tooltip
              title={t('benchmark_config')}
              placement="right-end"
              arrow
            >
              <InfoOutlined
                fontSize="small"
                sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
              />
            </Tooltip>
          </Box>
          {configureBenchmarking && platform && (
            <BenchmarkingQuickSetup
              platforms={platforms}
              platform={platform}
              companyUrl={companyUrl}
              competitors={competitors}
              setCompetitors={setCompetitors}
            />
          )}
        </Box>
      </Guide>
    </Section>
  );
};

export default ReviewMonitoringGuide;
